// src/components/SettingsPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Settings page — API Key management
// Industry standard pattern: prefix shown, full key shown once on generation,
// hash stored in Supabase. Never store or display the full key again.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient.js';

// ── Generate a cryptographically random client API key ──
function generateApiKey() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const randomPart = Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map(b => chars[b % chars.length]).join('');
  return `QAIS-CLIENT-${randomPart}`;
}

// ── SHA-256 hash for storing in Supabase (full key never stored) ──
async function sha256(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export default function SettingsPage({ user }) {
  const [keys, setKeys]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [generating, setGenerating]   = useState(false);
  const [newKey, setNewKey]           = useState(null);   // shown once after generation
  const [newKeyName, setNewKeyName]   = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [copied, setCopied]           = useState(false);
  const [revoking, setRevoking]       = useState(null);
  const [error, setError]             = useState('');
  // ── API examples tab UI state ──
  const [codeTab, setCodeTab] = useState('unix-kem');
  const [codeCopied, setCodeCopied]   = useState(false);

  // ── Load existing keys on mount ──
  useEffect(() => {
    loadKeys();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadKeys = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_api_keys')
        .select('id, key_prefix, name, created_at, last_used_at, is_active')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setKeys(data || []);
    } catch (err) {
      setError('Could not load API keys: ' + err.message);
    }
    setLoading(false);
  };

  // ── Generate new key ──
  const handleGenerate = async () => {
    if (!newKeyName.trim()) return;
    setGenerating(true);
    setError('');
    try {
      const fullKey = generateApiKey();
      const hash    = await sha256(fullKey);
      const prefix  = fullKey.slice(0, 20); // e.g. "QAIS-CLIENT-AbCdEfGh"

      const { error } = await supabase.from('client_api_keys').insert({
        user_id:      user.id,
        user_email:   user.email,
        key_hash:     hash,
        key_prefix:   prefix,
        name:         newKeyName.trim(),
        is_active:    true,
        created_at:   new Date().toISOString(),
      });
      if (error) throw error;

      setNewKey(fullKey);  // show once only
      setShowNameInput(false);
      setNewKeyName('');
      await loadKeys();
    } catch (err) {
      setError('Could not generate key: ' + err.message);
    }
    setGenerating(false);
  };

  // ── Revoke a key ──
  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this API key? Any systems using it will stop working immediately.')) return;
    setRevoking(id);
    try {
      const { error } = await supabase
        .from('client_api_keys')
        .update({ is_active: false })
        .eq('id', id);
      if (error) throw error;
      await loadKeys();
    } catch (err) {
      setError('Could not revoke key: ' + err.message);
    }
    setRevoking(null);
  };

  // ── Copy to clipboard ──
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const activeKeys   = keys.filter(k => k.is_active);
  const revokedKeys  = keys.filter(k => !k.is_active);

  // ── Styles ──
  const s = {
    page:      { maxWidth: '860px', margin: '0 auto', padding: '32px 24px', fontFamily: 'Arial, sans-serif' },
    card:      { background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '24px', marginBottom: '20px' },
    h1:        { fontSize: '24px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 4px' },
    h2:        { fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px' },
    sub:       { fontSize: '13px', color: '#64748b', margin: '0 0 20px', lineHeight: '1.6' },
    label:     { fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' },
    input:     { width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    btnPrimary:{ padding: '10px 20px', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
    btnGhost:  { padding: '8px 14px', backgroundColor: 'white', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
    btnRed:    { padding: '6px 12px', backgroundColor: 'white', color: '#ef4444', border: '1.5px solid #fecaca', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
    keyBox:    { background: '#0f172a', borderRadius: '10px', padding: '16px', fontFamily: 'monospace', fontSize: '13px', color: '#86efac', letterSpacing: '0.02em', wordBreak: 'break-all' },
    tag:       { display: 'inline-block', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' },
    error:     { background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#b91c1c', marginBottom: '16px' },
  };

  return (
    <div style={s.page}>

      {/* ── Header ── */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={s.h1}>⚙️ Settings</h1>
<p style={{ ...s.sub, margin: 0, fontSize:'22px' }}>Manage your API keys for submitting AI decisions programmatically.</p>
<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f0fdf4', border: '1.5px solid #10b981', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '700', color: '#065f46' }}>
    ⚛️ ML-KEM-768 (NIST FIPS 203)
  </span>
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#eff6ff', border: '1.5px solid #3b82f6', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '700', color: '#1e40af' }}>
    🔐 ML-DSA-65 (NIST FIPS 204)
  </span>
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#faf5ff', border: '1.5px solid #8b5cf6', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '700', color: '#6d28d9' }}>
    #️⃣ SHA3-256 (NIST FIPS 202)
  </span>
</div>
      </div>

      {error && <div style={s.error}>⚠️ {error}</div>}

      {/* ── NEW KEY REVEALED ── */}
      {newKey && (
        <div style={{ ...s.card, border: '2px solid #10b981', background: '#f0fdf4' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '20px' }}>🎉</span>
            <h2 style={{ ...s.h2, color: '#065f46', margin: 0 }}>API Key Generated — Copy it now!</h2>
          </div>
          <div style={{ background: '#fef9c3', border: '1.5px solid #fbbf24', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#92400e', marginBottom: '12px', lineHeight: '1.6' }}>
            ⚠️ <strong>This is the only time this key will be shown.</strong> Copy it now and store it securely. We only store a hash — we cannot show it again.
          </div>
          <div style={s.keyBox}>{newKey}</div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
            <button onClick={() => handleCopy(newKey)} style={{ ...s.btnPrimary, backgroundColor: copied ? '#059669' : '#1e40af' }}>
              {copied ? '✅ Copied!' : '📋 Copy to Clipboard'}
            </button>
            <button onClick={() => setNewKey(null)} style={s.btnGhost}>
              I have saved it — Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ── GENERATE NEW KEY ── */}
      <div style={s.card}>
      <h2 style={s.h2}>API Keys</h2>
      <p style={{ ...s.sub, fontSize: '16px' }}>
        Use these keys to submit AI audit decisions via the REST API. Each key is unique to your account.
        Include it as the <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '16px' }}>x-api-key</code> header in your requests.
        For maximum security use the <strong>⚛️ Quantum-Safe Flow</strong> with ML-KEM-768 (NIST FIPS 203) — see the How to Use section below.
      </p>

        {!showNameInput ? (
          <button onClick={() => setShowNameInput(true)} style={s.btnPrimary}>
            + Generate New API Key
          </button>
        ) : (
          <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '16px' }}>
            <label style={s.label}>Key name (helps you identify what this key is for)</label>
            <input
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGenerate()}
              placeholder="e.g. Production System, CI Pipeline, Partner Integration"
              style={s.input}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button onClick={handleGenerate} disabled={!newKeyName.trim() || generating}
                style={{ ...s.btnPrimary, opacity: !newKeyName.trim() || generating ? 0.5 : 1, cursor: !newKeyName.trim() || generating ? 'not-allowed' : 'pointer' }}>
                {generating ? '⏳ Generating...' : '🔑 Generate Key'}
              </button>
              <button onClick={() => { setShowNameInput(false); setNewKeyName(''); }} style={s.btnGhost}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── ACTIVE KEYS ── */}
      <div style={s.card}>
        <h2 style={s.h2}>Active Keys ({activeKeys.length})</h2>
        <p style={{ ...s.sub, fontSize: '16px' }}>These keys can be used to submit data to the API. Revoke any key that is no longer needed or may be compromised.</p>

        {loading ? (
          <p style={{ fontSize: '13px', color: '#94a3b8' }}>Loading keys...</p>
        ) : activeKeys.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔑</div>
            <p style={{ fontSize: '13px', margin: 0 }}>No active keys yet. Generate one above to get started.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Name', 'Key Preview', 'Created', 'Last Used', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1.5px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeKeys.map((k, i) => (
                <tr key={k.id} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={{ padding: '12px', fontSize: '13px', fontWeight: '600', color: '#0f172a', borderBottom: '1px solid #f1f5f9' }}>{k.name}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                    <code style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace', color: '#334155' }}>
                      {k.key_prefix}••••••••••••
                    </code>
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>{formatDate(k.created_at)}</td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>{formatDate(k.last_used_at)}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ ...s.tag, background: '#dcfce7', color: '#166534' }}>● Active</span>
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                    <button onClick={() => handleRevoke(k.id)} disabled={revoking === k.id} style={{ ...s.btnRed, opacity: revoking === k.id ? 0.5 : 1 }}>
                      {revoking === k.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── REVOKED KEYS ── */}
      {revokedKeys.length > 0 && (
        <div style={s.card}>
          <h2 style={s.h2}>Revoked Keys ({revokedKeys.length})</h2>
          <p style={s.sub}>These keys are no longer valid and cannot be used.</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Name', 'Key Preview', 'Created', 'Status'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1.5px solid #f1f5f9' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {revokedKeys.map((k, i) => (
                <tr key={k.id} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', opacity: 0.6 }}>
                  <td style={{ padding: '12px', fontSize: '13px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>{k.name}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                    <code style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace', color: '#94a3b8' }}>
                      {k.key_prefix}••••••••••••
                    </code>
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>{formatDate(k.created_at)}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ ...s.tag, background: '#fee2e2', color: '#991b1b' }}>✕ Revoked</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── USAGE GUIDE — Tabbed code samples ── */}

<div style={s.card}>
  <h2 style={s.h2}>How to Use Your API Key</h2>

  {/* ── Complete Flow Explanation ── */}
  <div style={{ marginBottom: '20px' }}>
    <p style={{ ...s.sub, fontSize: '14px', marginBottom: '12px' }}>
      Submit AI audit decisions programmatically. Two ways to integrate:
    </p>

    {/* Standard Flow */}
    <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
      <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
        🐧 Standard Flow
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '12px', color: '#475569' }}>
        <span style={{ background: '#e2e8f0', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>1. Generate API Key</span>
        <span>→</span>
        <span style={{ background: '#e2e8f0', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>2. POST AI Decision</span>
        <span>→</span>
        <span style={{ background: '#e2e8f0', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>3. ML-DSA-65 Signs It</span>
        <span>→</span>
        <span style={{ background: '#e2e8f0', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>4. Quantum-Safe Receipt</span>
      </div>
    </div>

    {/* Quantum-Safe Flow */}
    <div style={{ background: '#f0fdf4', border: '1.5px solid #10b981', borderRadius: '10px', padding: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#065f46' }}>
          ⚛️ Quantum-Safe Flow
        </p>
        <span style={{ background: '#10b981', color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px' }}>
          RECOMMENDED
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '12px', color: '#065f46' }}>
        <span style={{ background: '#dcfce7', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>1. Generate API Key</span>
        <span>→</span>
        <span style={{ background: '#dcfce7', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>2. GET ML-KEM-768 Public Key</span>
        <span>→</span>
        <span style={{ background: '#dcfce7', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>3. Encapsulate Secret</span>
        <span>→</span>
        <span style={{ background: '#dcfce7', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>4. POST Decision + Ciphertext</span>
        <span>→</span>
        <span style={{ background: '#dcfce7', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>5. ML-DSA-65 Signs It</span>
        <span>→</span>
        <span style={{ background: '#dcfce7', padding: '4px 10px', borderRadius: '6px', fontWeight: '600' }}>6. Quantum-Safe Receipt</span>
      </div>
      <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#065f46', lineHeight: '1.5' }}>
        ⚛️ Uses <strong>ML-KEM-768 (NIST FIPS 203)</strong> + <strong>ML-DSA-65 (NIST FIPS 204)</strong> + <strong>SHA3-256 (NIST FIPS 202)</strong> — the complete NIST post-quantum suite.
        Protects your data from <strong>Harvest Now, Decrypt Later (HNDL)</strong> attacks.
        Your API Key is required for both flows.
      </p>
    </div>
  </div>

  <p style={{ ...s.sub, fontSize: '16px' }}>
    Replace{' '}
    <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '16px' }}>QAIS-CLIENT-YOUR_KEY_HERE</code>{' '}
    with your generated key (shown once after generation).
  </p>

        {/* Tab buttons */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {[
          { id: 'unix-kem',  label: '⚛️ Unix · KEM ✨' },
          { id: 'ps-kem',    label: '⚛️ PowerShell · KEM ✨' },
          { id: 'unix-post', label: '🐧 Unix · POST' },
          { id: 'unix-get',  label: '🐧 Unix · GET' },
          { id: 'ps-post',   label: '🪟 PowerShell · POST' },
          { id: 'ps-get',    label: '🪟 PowerShell · GET' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setCodeTab(tab.id)}
              style={{
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '8px',
                cursor: 'pointer',
                border: codeTab === tab.id ? '1.5px solid #1e40af' : '1.5px solid #e2e8f0',
                background:  codeTab === tab.id ? '#1e40af' : 'white',
                color:       codeTab === tab.id ? 'white'   : '#475569',
                transition: 'all 0.15s ease',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Active tab content */}
        {(() => {
          const samples = {
            'unix-post': {
              note: 'Tip: append `| jq` for pretty JSON output. Install jq: `brew install jq` (Mac) · `apt install jq` (Linux).',
              code: `curl -s -X POST "https://api.quantumaishield.com/api/ai/decision" -H "Content-Type: application/json" -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" -d '{"company":"Your Company","ai_system":"LoanBot","decision_type":"loan_approval","decision":"APPROVED","confidence_score":95,"human_reviewed":false}' | jq`,
            },
            'unix-get': {
              note: 'Lists your AI decisions. Filter by company, decision_type, from_date, to_date.',
              code: `curl -s -X GET "https://api.quantumaishield.com/api/ai/decisions?company=Your%20Company&limit=10" -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" | jq`,
            },
            'ps-post': {
              note: 'PowerShell native — no extra install needed. Output is auto-formatted JSON.',
              code: `curl.exe -s -X POST "https://api.quantumaishield.com/api/ai/decision" -H "Content-Type: application/json" -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" -d "{\\"company\\":\\"Your Company\\",\\"ai_system\\":\\"LoanBot\\",\\"decision_type\\":\\"loan_approval\\",\\"decision\\":\\"APPROVED\\",\\"confidence_score\\":95,\\"human_reviewed\\":false}" | ConvertFrom-Json | ConvertTo-Json -Depth 10`,
            },

            'ps-get': {
              note: 'Use -Depth 10 so nested JSON renders fully (PowerShell default is 2 levels).',
              code: `curl.exe -s -X GET "https://api.quantumaishield.com/api/ai/decisions?company=Your%20Company&limit=10" -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" | ConvertFrom-Json | ConvertTo-Json -Depth 10`,
            },
            'unix-kem': {
              recommended: true,
              note: '⚛️ Recommended — ML-KEM-768 (NIST FIPS 203) quantum-safe key exchange protects your AI decision data from Harvest Now Decrypt Later (HNDL) attacks.',
              code: `# ── SETUP (one-time) ──────────────────────────────────
            # Install jq for readable output (skip if already installed)
            # Mac:   brew install jq
            # Linux: sudo apt install jq

            # ── STEP 1 — Get quantum-safe public key ──────────────
            # Run once per session to get a fresh ML-KEM-768 key
            curl -s -X GET "https://api.quantumaishield.com/api/kem/public-key" \\
              -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" | jq

            # → Copy the "public_key" value from the response

            # ── STEP 2 — Encapsulate shared secret ────────────────
            # Replace YOUR_PUBLIC_KEY with the value from Step 1
            curl -s -X POST "https://api.quantumaishield.com/api/kem/encapsulate" \\
              -H "Content-Type: application/json" \\
              -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" \\
              -d '{"public_key":"YOUR_PUBLIC_KEY_FROM_STEP_1"}' | jq

            # → Copy the "ciphertext" value from the response

            # ── STEP 3 — Submit AI decision (quantum-safe) ────────
            # Replace YOUR_CIPHERTEXT with the value from Step 2
            curl -s -X POST "https://api.quantumaishield.com/api/ai/decision" \\
              -H "Content-Type: application/json" \\
              -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" \\
              -H "x-kem-ciphertext: YOUR_CIPHERTEXT_FROM_STEP_2" \\
              -d '{"company":"Your Company","ai_system":"LoanBot","decision_type":"loan_approval","decision":"APPROVED","confidence_score":95,"human_reviewed":false}' | jq

            # ── TEST — Verify quantum-safe receipt ────────────────
            # Check your decision was signed with ML-DSA-65 + KEM protected
            curl -s -X GET "https://api.quantumaishield.com/api/ai/decisions?company=Your%20Company&limit=1" \\
              -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" | jq '.decisions[0].quantum_security'`,
            },
            'ps-kem': {
              recommended: true,
              note: '⚛️ Recommended — ML-KEM-768 (NIST FIPS 203) quantum-safe key exchange for PowerShell. Protects sensitive AI decision data from quantum computer attacks.',
              code: `# ── SETUP (one-time) ──────────────────────────────────
            # No extra installs needed — PowerShell built-in only

            # ── STEP 1 — Get quantum-safe public key ──────────────
            $step1 = curl.exe -s -X GET \`
              "https://api.quantumaishield.com/api/kem/public-key" \`
              -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" | ConvertFrom-Json
            # Copy $step1.public_key value
            Write-Host "Public Key:" $step1.public_key.Substring(0,32) "..."

            # ── STEP 2 — Encapsulate shared secret ────────────────
            $step2 = curl.exe -s -X POST \`
              "https://api.quantumaishield.com/api/kem/encapsulate" \`
              -H "Content-Type: application/json" \`
              -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" \`
              -d (@{public_key=$step1.public_key} | ConvertTo-Json) | ConvertFrom-Json
            # Copy $step2.ciphertext value
            Write-Host "Ciphertext:" $step2.ciphertext.Substring(0,32) "..."

            # ── STEP 3 — Submit AI decision (quantum-safe) ────────
            $step3 = curl.exe -s -X POST \`
              "https://api.quantumaishield.com/api/ai/decision" \`
              -H "Content-Type: application/json" \`
              -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" \`
              -H "x-kem-ciphertext: $($step2.ciphertext)" \`
              -d (@{
                company="Your Company"; ai_system="LoanBot"
                decision_type="loan_approval"; decision="APPROVED"
                confidence_score=95; human_reviewed=$false
              } | ConvertTo-Json) | ConvertFrom-Json
            $step3 | ConvertTo-Json -Depth 10

            # ── TEST — Verify quantum-safe receipt ────────────────
            curl.exe -s -X GET \`
              "https://api.quantumaishield.com/api/ai/decisions?company=Your%20Company&limit=1" \`
              -H "x-api-key: QAIS-CLIENT-YOUR_KEY_HERE" | ConvertFrom-Json | ConvertTo-Json -Depth 10`,
            },


          };
          const active = samples[codeTab];

          return (
            <>
              {active.recommended && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1.5px solid #10b981',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  marginBottom: '12px',
                  fontSize: '12px',
                  color: '#065f46',
                  lineHeight: '1.6'
                }}>
                  ⚛️ <strong>Recommended — Quantum-Safe Channel</strong>
                  {' '}This method uses ML-KEM-768 (NIST FIPS 203) + ML-DSA-65 (NIST FIPS 204)
                  — the complete NIST post-quantum suite. Protects your data from
                  {' '}<strong>Harvest Now, Decrypt Later</strong> attacks.
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <pre
                  style={{
                    ...s.keyBox,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0,
                    paddingRight: '90px', // room for Copy button
                    lineHeight: '1.6',
                  }}
                >
                  {active.code}
                </pre>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(active.code);
                    setCodeCopied(true);
                    setTimeout(() => setCodeCopied(false), 2000);
                  }}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    padding: '6px 12px',
                    fontSize: '11px',
                    fontWeight: '700',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: 'none',
                    background: codeCopied ? '#059669' : '#334155',
                    color: 'white',
                    transition: 'background 0.15s ease',
                  }}
                >
                  {codeCopied ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '10px 0 0', lineHeight: '1.5' }}>
                {active.note}
              </p>
            </>
          );
        })()}

        {/* Field reference */}
        <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', margin: '24px 0 10px' }}>Field Reference</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[
            { field: 'company',           values: 'Must match your sealed compliance record' },
            { field: 'ai_system',         values: 'Free text (e.g., LoanBot, MedTriage)' },
            { field: 'decision_type',     values: 'loan_approval, diagnosis, fraud_flag, cv_screening, etc.' },
            { field: 'decision',          values: 'APPROVED, DENIED, FLAGGED, PASSED, LOGGED' },
            { field: 'confidence_score',  values: '0–100 (optional)' },
            { field: 'human_reviewed',    values: 'true / false (optional)' },
            { field: 'framework',         values: 'EU AI Act, ISO 42001, NIST AI RMF (optional)' },
            { field: 'risk_level',        values: 'LOW, MEDIUM, HIGH, CRITICAL (optional)' },
          ].map(({ field, values }) => (
            <div key={field} style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px' }}>
              <p style={{ fontSize: '11px', fontWeight: '700', color: '#475569', margin: '0 0 3px', textTransform: 'uppercase' }}>{field}</p>
              <p style={{ fontSize: '12px', color: '#0f172a', margin: 0, fontFamily: 'monospace' }}>{values}</p>
            </div>
          ))}
        </div>

        {/* Precondition reminder */}
        <div style={{ background: '#fef3c7', border: '1.5px solid #fbbf24', borderRadius: '8px', padding: '10px 14px', marginTop: '16px' }}>
          <p style={{ margin: 0, color: '#92400e', fontSize: '12px', lineHeight: '1.5' }}>
            <strong>⚠️ Precondition:</strong> You must seal a compliance record for the same <code style={{ background: '#fde68a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>company</code> first
            (via the Quantum Ledger flow) before logging AI decisions. Otherwise the API returns <code style={{ background: '#fde68a', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace' }}>412 No compliance baseline</code>.
          </p>
        </div>
      </div>

    </div>
  );
}
